/**
 * ADA High Contrast Control - WCAG 2.2 Compliant - Mobile menu fix
 * Auto-detects OS preference and provides manual toggle
 */

(function() {
  'use strict';

  const contrastToggleButtons = [];
  let primaryContrastButton = null;

  // Storage key for localStorage
  const STORAGE_KEY = 'ada-contrast-mode';
  
  // Contrast modes - simplified to on/off only
  const MODES = {
    OFF: 'off',
    ON: 'on'
  };

  // AudioEye widget - completely removed, no styling applied

  // Initialize contrast control
  function initContrastControl() {
    // Create primary toggle button for header
    const headerToggle = createToggleButton('ada-accessibility-options');
    primaryContrastButton = headerToggle;

    const cartLink = document.querySelector('a[href="/cart"]');
    if (cartLink && cartLink.parentElement) {
      cartLink.parentElement.insertBefore(headerToggle, cartLink);
    } else {
      const header = document.querySelector('header');
      if (header) {
        const headerRight = header.querySelector('.header-actions') || header.querySelector('.flex.justify-end');
        if (headerRight) {
          headerRight.insertBefore(headerToggle, headerRight.firstChild);
        } else {
          header.appendChild(headerToggle);
        }
      }
    }
    registerContrastToggle(headerToggle);

    // Create toggle within mobile menu if placeholder exists
    let mobileMenuButtonCreated = false; // Flag to prevent duplicate creation
    function createMobileMenuButton() {
      // Don't create if already created
      if (mobileMenuButtonCreated) {
        return false;
      }
      
      const mmContrastTarget = document.querySelector('[data-mm-contrast-target]');
      console.log('createMobileMenuButton: Looking for [data-mm-contrast-target]', mmContrastTarget);
      if (mmContrastTarget) {
        // Check if button already exists (don't create duplicate)
        const existing = mmContrastTarget.querySelector('.ada-contrast-toggle');
        if (existing) {
          console.log('createMobileMenuButton: Button already exists, skipping');
          mobileMenuButtonCreated = true;
          return true;
        }
        
        const menuToggle = createToggleButton('ada-accessibility-options-mobile');
        menuToggle.setAttribute('data-context', 'mobile-menu');
        const labelId = mmContrastTarget.getAttribute('data-label-id');
        if (labelId) {
          menuToggle.setAttribute('aria-labelledby', labelId);
        }
        
        // CRITICAL: Apply all styles inline to match login button EXACTLY - use same classes and styles
        // NOTE: Don't set background-color and color with !important here - they'll be updated dynamically
        menuToggle.className = 'mm-button uppercase tracking-1_5px font-bold text-center border-2 border-black bg-white text-black hover:bg-black hover:text-white transition-all duration-200';
        menuToggle.style.cssText = 'opacity: 1 !important; visibility: visible !important; display: flex !important; width: 100% !important; height: 44px !important; max-height: 44px !important; min-height: 44px !important; font-size: 14px !important; line-height: 1.2 !important; padding: 10px 16px !important; box-sizing: border-box !important; border-radius: 4px !important; border: 2px solid #000000 !important; font-weight: bold !important; text-transform: uppercase !important; letter-spacing: 1.5px !important; align-items: center !important; justify-content: center !important; text-decoration: none !important; cursor: pointer !important;';
        // Set initial colors without !important so they can be updated dynamically
        menuToggle.style.setProperty('background-color', '#ffffff', 'important');
        menuToggle.style.setProperty('color', '#000000', 'important');
        
        registerContrastToggle(menuToggle);
        mmContrastTarget.appendChild(menuToggle);
        mobileMenuButtonCreated = true; // Mark as created
        console.log('createMobileMenuButton: Button created and appended', menuToggle);
        return true;
      } else {
        console.log('createMobileMenuButton: [data-mm-contrast-target] not found');
      }
      return false;
    }
    
    // Try immediately and on various events
    function tryCreateButton() {
      if (!mobileMenuButtonCreated) {
        createMobileMenuButton();
      }
    }
    
    tryCreateButton();
    setTimeout(tryCreateButton, 100);
    setTimeout(tryCreateButton, 500);
    setTimeout(tryCreateButton, 1000);
    
    // Listen for menu open
    document.addEventListener('click', function(e) {
      if (e.target.closest('.js-toggle-mm, .js-open-mm, [data-mobile-menu]')) {
        setTimeout(tryCreateButton, 50);
        setTimeout(tryCreateButton, 200);
      }
    });
    
    // Watch for body class changes (mm--open)
    const bodyObserver = new MutationObserver(function(mutations) {
      if (document.body.classList.contains('mm--open')) {
        setTimeout(tryCreateButton, 50);
      }
    });
    bodyObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    // Create footer toggle - always visible for discoverability
    const footerContrastTarget = document.querySelector('[data-footer-contrast-target]');
    if (footerContrastTarget) {
      const footerToggle = createToggleButton('ada-accessibility-options-footer');
      footerToggle.setAttribute('data-context', 'footer');
      footerToggle.classList.add('ada-footer-control'); // Add class for CSS targeting
      registerContrastToggle(footerToggle);
      footerContrastTarget.appendChild(footerToggle);
    }
    
    // Load saved preference or detect OS preference
    const savedMode = localStorage.getItem(STORAGE_KEY);
    const osPreference = detectOSPreference();
    
    let initialMode = MODES.OFF;
    if (savedMode && savedMode !== MODES.OFF) {
      // Convert old AA/AAA modes to ON
      if (savedMode === 'aa' || savedMode === 'aaa') {
        initialMode = MODES.ON;
      } else {
        initialMode = savedMode;
      }
    } else if (osPreference) {
      initialMode = osPreference;
    }
    
    // Apply initial mode
    if (initialMode !== MODES.OFF) {
      applyContrastMode(initialMode);
    } else {
      injectContrastStyles(MODES.OFF);
    }
    updateAllContrastButtons(initialMode);
    
    // Listen for OS preference changes
    if (window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-contrast: high)');
      mediaQuery.addEventListener('change', function(e) {
        if (!localStorage.getItem(STORAGE_KEY)) {
          if (e.matches) {
            applyContrastMode(MODES.ON);
            updateAllContrastButtons(MODES.ON);
          } else {
            removeContrastMode();
            updateAllContrastButtons(MODES.OFF);
          }
        }
      });
    }
    
    // Handle footer link to reveal toggle
    setupAccessibilityLink(primaryContrastButton);
  }
  
  // Setup footer link to reveal toggle
  function setupAccessibilityLink(toggleButton) {
    if (!toggleButton) return;
    // Wait for DOM to be ready
    function attachLinkHandler() {
      const accessibilityLink = document.getElementById('ada-accessibility-link');
      if (accessibilityLink) {
        accessibilityLink.addEventListener('click', function(e) {
          e.preventDefault();
          // Show the toggle
          toggleButton.classList.add('ada-toggle-visible');
          // Scroll to toggle button
          setTimeout(function() {
            toggleButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Focus the toggle button
            toggleButton.focus();
          }, 100);
        });
      } else {
        // Retry if link doesn't exist yet (footer might load later)
        setTimeout(attachLinkHandler, 100);
      }
    }
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', attachLinkHandler);
    } else {
      attachLinkHandler();
    }
  }

  function registerContrastToggle(button) {
    if (!button) return;
    // Check if button is already registered to prevent duplicates
    if (contrastToggleButtons.indexOf(button) === -1) {
      contrastToggleButtons.push(button);
      button.addEventListener('click', handleContrastToggleClick);
      button.addEventListener('keydown', handleContrastToggleKeydown);
      if (button.dataset && button.dataset.context === 'mobile-menu') {
        button.classList.add('ada-toggle-visible', 'w-full');
      }
    }
  }

  function handleContrastToggleClick(event) {
    event.preventDefault();
    toggleContrastMode();
  }

  function handleContrastToggleKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleContrastMode();
    }
  }

  // Create toggle button
  function createToggleButton(id = 'ada-accessibility-options') {
    const button = document.createElement('button');
    if (id) {
      button.id = id;
    }
    button.className = 'ada-contrast-toggle';
    
    // Check if this is for mobile menu
    const isMobileMenu = button.dataset && button.dataset.context === 'mobile-menu';
    
    // Enhanced aria-label for ADA compliance
    if (isMobileMenu) {
      button.setAttribute('aria-label', 'Toggle high contrast mode. Currently off. Press to enable high contrast for better visibility.');
    } else {
      button.setAttribute('aria-label', 'Toggle high contrast mode');
    }
    
    button.setAttribute('aria-pressed', 'false');
    button.setAttribute('role', 'button');
    button.setAttribute('tabindex', '0');
    
    const icon = document.createElement('span');
    icon.className = 'ada-contrast-toggle-icon';
    icon.innerHTML = getContrastIcon();
    icon.setAttribute('aria-hidden', 'true');
    
    const text = document.createElement('span');
    text.className = 'ada-contrast-toggle-text';
    // For mobile menu, use shorter text to match button style
    if (isMobileMenu) {
      text.textContent = 'HIGH CONTRAST';
    } else {
      text.textContent = 'High Contrast';
    }
    
    button.appendChild(icon);
    button.appendChild(text);
    
    // Add mobile menu styling class if in mobile menu context
    if (isMobileMenu) {
      button.classList.add('mm-button-style');
    }
    
    return button;
  }

  // Get contrast icon SVG
  function getContrastIcon() {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5H10.9v1.69c-1.51.32-2.72 1.3-2.72 2.81 0 1.79 1.49 2.69 3.66 3.21 1.95.46 2.34 1.15 2.34 1.87 0 .53-.39 1.39-2.1 1.39-1.6 0-2.23-.72-2.32-1.64H8.04c.1 1.7 1.36 2.66 2.86 2.97V19h2.34v-1.67c1.52-.29 2.72-1.16 2.72-2.81 0-1.65-1.08-2.46-3.31-2.87z"/>
      </svg>
    `;
  }

  // Detect OS preference
  function detectOSPreference() {
    if (window.matchMedia && window.matchMedia('(prefers-contrast: high)').matches) {
      return MODES.ON; // Default to ON when OS preference is detected
    }
    return null;
  }

  // Toggle contrast mode - simplified to on/off
  function toggleContrastMode() {
    const currentMode = document.documentElement.getAttribute('data-contrast-mode');
    
    let nextMode;
    if (!currentMode || currentMode === MODES.OFF) {
      nextMode = MODES.ON;
    } else {
      nextMode = MODES.OFF;
    }
    
    if (nextMode === MODES.OFF) {
      removeContrastMode();
      localStorage.removeItem(STORAGE_KEY);
    } else {
      applyContrastMode(nextMode);
      localStorage.setItem(STORAGE_KEY, nextMode);
    }
    
    updateAllContrastButtons(nextMode);
    
    // Update aria-labels for mobile menu buttons
    const modeText = nextMode === MODES.OFF ? 'off' : 'on';
    contrastToggleButtons.forEach(function(button) {
      if (button.dataset && button.dataset.context === 'mobile-menu') {
        const isActive = nextMode !== MODES.OFF;
        button.setAttribute('aria-label', `Toggle high contrast mode. Currently ${modeText}. Press to ${isActive ? 'disable' : 'enable'} high contrast.`);
      }
    });
    
    // Announce change to screen readers
    announceContrastChange(nextMode);
  }

  // Apply contrast mode
  function applyContrastMode(mode) {
    document.documentElement.setAttribute('data-contrast-mode', mode);
    
    // Force apply styles via injected style tag for maximum compatibility
    injectContrastStyles(mode);
    
    // CRITICAL: Override inline filter styles on minus signs using JavaScript
    // Inline styles with !important can't be overridden by CSS, so we use JS
    if (mode !== MODES.OFF) {
      setTimeout(function() {
        const minusSigns = document.querySelectorAll('.mm img[src*="Minus.svg"].mm-child-minus, .mm .sub-menu img[src*="Minus.svg"], .mm ul li img[src*="Minus.svg"]');
        minusSigns.forEach(function(img) {
          if (img.classList.contains('mm-child-minus') || img.closest('.sub-menu') || img.closest('ul li')) {
            img.style.setProperty('filter', 'brightness(0) saturate(100%) invert(0%)', 'important');
            img.style.setProperty('-webkit-filter', 'brightness(0) saturate(100%) invert(0%)', 'important');
          }
        });
        
        // CRITICAL: Override inline styles and CSS variables on nav bars
        // Nav bars use inline style attributes and CSS variables that need JS to override
        const navBars = document.querySelectorAll('.text-bar, .ch-bar, .asg-nav-bar');
        navBars.forEach(function(navBar) {
          // Override inline background styles
          navBar.style.setProperty('background-color', '#000000', 'important');
          navBar.style.setProperty('background', '#000000', 'important');
          navBar.style.setProperty('backdrop-filter', 'none', 'important');
          navBar.style.setProperty('-webkit-backdrop-filter', 'none', 'important');
          
          // Override CSS variables
          navBar.style.setProperty('--bar-txt-color', '#ffffff', 'important');
          navBar.style.setProperty('--nav-text-color', '#ffffff', 'important');
          
          // Override all text elements inside nav bars
          const textElements = navBar.querySelectorAll('*');
          textElements.forEach(function(el) {
            el.style.setProperty('color', '#ffffff', 'important');
            el.style.setProperty('--bar-txt-color', '#ffffff', 'important');
            el.style.setProperty('--nav-text-color', '#ffffff', 'important');
          });
        });
      }, 100);
    }
    
    // Force header to white when contrast mode is enabled
    if (mode !== MODES.OFF) {
      // Use requestAnimationFrame to ensure this runs after styles are injected
      requestAnimationFrame(function() {
        requestAnimationFrame(function() {
          if (typeof window.setHeaderBackground === 'function') {
            window.setHeaderBackground(true);
          }
          // Also directly set header background with !important to override any other styles
          const header = document.querySelector('header');
          const headerContainer = document.querySelector('.header-container');
          const headerMain = document.querySelector('.header-main');
          const headerNav = document.querySelector('.header-nav');
          const allHeaderElements = [header, headerContainer, headerMain, headerNav].filter(Boolean);
          const whiteBg = 'rgba(255, 255, 255, 1)';
          allHeaderElements.forEach((el) => {
            if (el) {
              el.style.setProperty('background-color', whiteBg, 'important');
              el.style.setProperty('background', whiteBg, 'important');
            }
          });
        });
      });
    }
    
    // AudioEye widget - no styling applied
  }
  
  // Monitor header background changes and force white when contrast mode is enabled
  let headerObserver = null;
  function setupHeaderObserver() {
    if (headerObserver) {
      headerObserver.disconnect();
    }
    
    const contrastMode = document.documentElement.getAttribute('data-contrast-mode');
    if (!contrastMode || contrastMode === MODES.OFF) {
      return; // Don't observe when contrast mode is off
    }
    
    const header = document.querySelector('header');
    if (!header) {
      setTimeout(setupHeaderObserver, 100);
      return;
    }
    
    headerObserver = new MutationObserver(function(mutations) {
      const currentMode = document.documentElement.getAttribute('data-contrast-mode');
      if (!currentMode || currentMode === MODES.OFF) {
        headerObserver.disconnect();
        return;
      }
      
      // Check if header background was changed away from white
      const headerBg = window.getComputedStyle(header).backgroundColor;
      const isWhite = headerBg.includes('255') || headerBg === 'rgb(255, 255, 255)' || headerBg === 'rgba(255, 255, 255, 1)';
      
      if (!isWhite) {
        const whiteBg = 'rgba(255, 255, 255, 1)';
        [header, document.querySelector('.header-container'), document.querySelector('.header-main'), document.querySelector('.header-nav')]
          .filter(Boolean)
          .forEach((el) => {
            el.style.setProperty('background-color', whiteBg, 'important');
            el.style.setProperty('background', whiteBg, 'important');
          });
      }
    });
    
    headerObserver.observe(header, {
      attributes: true,
      attributeFilter: ['style', 'class'],
      childList: false,
      subtree: false
    });
    
    // Also observe the document element for contrast mode changes
    const docObserver = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-contrast-mode') {
          const newMode = document.documentElement.getAttribute('data-contrast-mode');
          if (newMode && newMode !== MODES.OFF) {
            setupHeaderObserver();
          } else {
            if (headerObserver) {
              headerObserver.disconnect();
            }
          }
        }
      });
    });
    
    docObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-contrast-mode']
    });
  }
  
  // Setup observer after initial contrast mode is applied
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      setTimeout(setupHeaderObserver, 100);
    });
  } else {
    setTimeout(setupHeaderObserver, 100);
  }
  
  // Inject aggressive contrast styles
  function injectContrastStyles(mode) {
    // Remove existing injected styles
    const existingStyle = document.getElementById('ada-contrast-injected');
    if (existingStyle) {
      existingStyle.remove();
    }
    
    if (mode === MODES.OFF) {
      return; // Don't inject styles when off
    }
    
    const style = document.createElement('style');
    style.id = 'ada-contrast-injected';
    style.textContent = `
      /* Aggressive contrast overrides */
      html[data-contrast-mode="${mode}"] body,
      html[data-contrast-mode="${mode}"] main,
      html[data-contrast-mode="${mode}"] section,
      html[data-contrast-mode="${mode}"] div,
      html[data-contrast-mode="${mode}"] p,
      html[data-contrast-mode="${mode}"] span,
      html[data-contrast-mode="${mode}"] h1,
      html[data-contrast-mode="${mode}"] h2,
      html[data-contrast-mode="${mode}"] h3,
      html[data-contrast-mode="${mode}"] h4,
      html[data-contrast-mode="${mode}"] h5,
      html[data-contrast-mode="${mode}"] h6,
      html[data-contrast-mode="${mode}"] li,
      html[data-contrast-mode="${mode}"] td,
      html[data-contrast-mode="${mode}"] th {
        color: #000000 !important;
      }
      
      /* CRITICAL: Override global black text rule for active contrast button - MUST come after global rules */
      /* Active contrast button MUST have white text/icons for visibility */
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle.active,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle[aria-pressed="true"],
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle.active *,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle[aria-pressed="true"] *,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle.active span,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle[aria-pressed="true"] span,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle.active .ada-contrast-toggle-text,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle.active .ada-contrast-toggle-icon,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle[aria-pressed="true"] .ada-contrast-toggle-text,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle[aria-pressed="true"] .ada-contrast-toggle-icon {
        color: #ffffff !important;
      }
      
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle.active svg,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle[aria-pressed="true"] svg,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle.active svg *,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle[aria-pressed="true"] svg *,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle.active svg path,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle[aria-pressed="true"] svg path {
        fill: #ffffff !important;
        stroke: #ffffff !important;
        color: #ffffff !important;
      }
      
      /* Only change backgrounds where text contrast would be poor */
      html[data-contrast-mode="${mode}"] body,
      html[data-contrast-mode="${mode}"] main {
        background-color: #ffffff !important;
      }
      
      /* Force header to white background in contrast mode */
      html[data-contrast-mode="${mode}"] header,
      html[data-contrast-mode="${mode}"] .header-container,
      html[data-contrast-mode="${mode}"] .header-main,
      html[data-contrast-mode="${mode}"] .header-nav {
        background-color: #ffffff !important;
        background: #ffffff !important;
      }
      
      /* Dark backgrounds keep dark but ensure light text */
      html[data-contrast-mode="${mode}"] [class*="bg-black"],
      html[data-contrast-mode="${mode}"] [class*="bg-black"] * {
        color: #ffffff !important;
      }
      
      /* Only change colored backgrounds if they don't have proper text contrast */
      html[data-contrast-mode="${mode}"] [class*="bg-red"]:not([class*="text-white"]),
      html[data-contrast-mode="${mode}"] [class*="bg-blue"]:not([class*="text-white"]),
      html[data-contrast-mode="${mode}"] [class*="bg-gray"]:not([class*="text-white"]) {
        background-color: #ffffff !important;
      }
      
      /* Footer links - ALL footer links should be blue with underline (highest priority, must come first) */
      html[data-contrast-mode="${mode}"] footer a,
      html[data-contrast-mode="${mode}"] section[class*="footer"] a,
      html[data-contrast-mode="${mode}"] footer section a {
        color: #0000ff !important;
        text-decoration: underline !important;
      }
      
      html[data-contrast-mode="${mode}"] footer a:hover,
      html[data-contrast-mode="${mode}"] section[class*="footer"] a:hover,
      html[data-contrast-mode="${mode}"] footer section a:hover {
        color: #0000ff !important;
        text-decoration: underline !important;
      }
      
      html[data-contrast-mode="${mode}"] footer a:visited,
      html[data-contrast-mode="${mode}"] section[class*="footer"] a:visited,
      html[data-contrast-mode="${mode}"] footer section a:visited {
        color: #0000ff !important;
        text-decoration: underline !important;
      }
      
      /* Links - but exclude navigation menu items, mobile menu, AND footer (footer handled above) */
      html[data-contrast-mode="${mode}"] a:not(.header-nav a):not(nav a):not(.megamenu a):not(.text-bar-link):not(.ch-link):not(.asg-nav-link):not(footer a):not(section[class*="footer"] a):not(.mm a):not([class*="mm-"]) {
        color: #0000ff !important;
      }
      
      html[data-contrast-mode="${mode}"] a:not(.header-nav a):not(nav a):not(.megamenu a):not(.text-bar-link):not(.ch-link):not(.asg-nav-link):not(footer a):not(section[class*="footer"] a):not(.mm a):not([class*="mm-"]):hover {
        color: #0000ff !important;
      }
      
      html[data-contrast-mode="${mode}"] a:not(.header-nav a):not(nav a):not(.megamenu a):not(.text-bar-link):not(.ch-link):not(.asg-nav-link):not(footer a):not(section[class*="footer"] a):not(.mm a):not([class*="mm-"]):visited {
        color: #0000ff !important;
      }
      
      /* Mobile menu links should be black in high contrast mode - NO BLUE OR PURPLE */
      html[data-contrast-mode="${mode}"] .mm a,
      html[data-contrast-mode="${mode}"] .mm a:hover,
      html[data-contrast-mode="${mode}"] .mm a:visited,
      html[data-contrast-mode="${mode}"] .mm a:active,
      html[data-contrast-mode="${mode}"] .mm a:link,
      html[data-contrast-mode="${mode}"] .mm li a,
      html[data-contrast-mode="${mode}"] .mm ul a,
      html[data-contrast-mode="${mode}"] .mm .sub-menu a {
        color: #000000 !important;
      }
      
      /* Navigation menu links should be black text - NO BLUE OR PURPLE */
      html[data-contrast-mode="${mode}"] .header-nav a,
      html[data-contrast-mode="${mode}"] nav a,
      html[data-contrast-mode="${mode}"] .megamenu a,
      html[data-contrast-mode="${mode}"] .header-nav li a,
      html[data-contrast-mode="${mode}"] .megamenu-trigger {
        color: #000000 !important;
      }
      
      html[data-contrast-mode="${mode}"] .header-nav a:hover,
      html[data-contrast-mode="${mode}"] nav a:hover,
      html[data-contrast-mode="${mode}"] .megamenu a:hover,
      html[data-contrast-mode="${mode}"] .header-nav a:visited,
      html[data-contrast-mode="${mode}"] nav a:visited,
      html[data-contrast-mode="${mode}"] .megamenu a:visited {
        color: #000000 !important;
        text-decoration: underline !important;
      }
      
      /* Force all SVG arrows in menu to black */
      html[data-contrast-mode="${mode}"] .header-nav-arrow-parent,
      html[data-contrast-mode="${mode}"] .header-nav-arrow-parent svg,
      html[data-contrast-mode="${mode}"] .header-nav-arrow-parent path,
      html[data-contrast-mode="${mode}"] .header-nav-arrow-child,
      html[data-contrast-mode="${mode}"] .header-nav-arrow-child svg,
      html[data-contrast-mode="${mode}"] .header-nav-arrow-child path,
      html[data-contrast-mode="${mode}"] .mm .sub-menu-icon img,
      html[data-contrast-mode="${mode}"] .mm .sub-menu-icon svg,
      html[data-contrast-mode="${mode}"] .mm .sub-menu-icon path,
      html[data-contrast-mode="${mode}"] .mm img[src*="Minus.svg"],
      html[data-contrast-mode="${mode}"] .mm img[src*="Plus.svg"],
      html[data-contrast-mode="${mode}"] .mm .sub-menu-icon .plus img,
      html[data-contrast-mode="${mode}"] .mm .sub-menu-icon .minus img {
        filter: brightness(0) saturate(100%) invert(0%) !important;
        color: #000000 !important;
        fill: #000000 !important;
        stroke: #000000 !important;
      }
      
      /* CRITICAL: Force child menu minus signs to black in high contrast mode - override inline styles with maximum specificity */
      html[data-contrast-mode="${mode}"] .mm .sub-menu img[src*="Minus.svg"],
      html[data-contrast-mode="${mode}"] .mm .sub-menu .mm-child-minus,
      html[data-contrast-mode="${mode}"] .mm ul img[src*="Minus.svg"],
      html[data-contrast-mode="${mode}"] .mm .sub-menu img.mm-child-minus,
      html[data-contrast-mode="${mode}"] .mm ul li img[src*="Minus.svg"],
      html[data-contrast-mode="${mode}"] .mm .sub-menu ul img[src*="Minus.svg"],
      html[data-contrast-mode="${mode}"] .mm .sub-menu ul li img[src*="Minus.svg"],
      html[data-contrast-mode="${mode}"] .mm .tracking-1px img[src*="Minus.svg"],
      html[data-contrast-mode="${mode}"] .mm .pl-2 img[src*="Minus.svg"],
      html[data-contrast-mode="${mode}"] .mm .pl-5 img[src*="Minus.svg"],
      html[data-contrast-mode="${mode}"] .mm li img[src*="Minus.svg"],
      html[data-contrast-mode="${mode}"] .mm li .mm-child-minus,
      html[data-contrast-mode="${mode}"] .mm li img.mm-child-minus[src*="Minus.svg"],
      html[data-contrast-mode="${mode}"] .mm ul li img.mm-child-minus[src*="Minus.svg"],
      html[data-contrast-mode="${mode}"] .mm .sub-menu ul li img.mm-child-minus[src*="Minus.svg"] {
        filter: brightness(0) saturate(100%) invert(0%) !important;
        -webkit-filter: brightness(0) saturate(100%) invert(0%) !important;
        color: #000000 !important;
        fill: #000000 !important;
        stroke: #000000 !important;
      }
      
      /* Subscribe buttons - preserve white text on black background (already has good contrast) */
      html[data-contrast-mode="${mode}"] button[value*="Subscribe"],
      html[data-contrast-mode="${mode}"] button[value*="SUBSCRIBE"],
      html[data-contrast-mode="${mode}"] button[name*="subscribe"],
      html[data-contrast-mode="${mode}"] button[name*="Subscribe"],
      html[data-contrast-mode="${mode}"] input[type="submit"][value*="Subscribe"],
      html[data-contrast-mode="${mode}"] input[type="submit"][value*="SUBSCRIBE"],
      html[data-contrast-mode="${mode}"] input[type="submit"][name*="subscribe"],
      html[data-contrast-mode="${mode}"] input[type="submit"][name*="Subscribe"],
      html[data-contrast-mode="${mode}"] button.bg-black.text-white,
      html[data-contrast-mode="${mode}"] input[type="submit"].bg-black.text-white {
        color: #ffffff !important;
        background-color: #000000 !important;
        background: #000000 !important;
      }
      
      /* Buttons - only ensure text contrast, don't force black - exclude both toggle buttons, subscribe buttons, and video controls */
      html[data-contrast-mode="${mode}"] button:not(.ada-contrast-toggle):not(.ada-global-pause-toggle):not(.ada-video-control):not([class*="bg-white"]):not([style*="background: #fff"]):not([style*="background-color: #fff"]):not([value*="Subscribe"]):not([value*="SUBSCRIBE"]):not([name*="subscribe"]):not([name*="Subscribe"]):not(.bg-black.text-white),
      html[data-contrast-mode="${mode}"] input[type="submit"]:not([value*="Subscribe"]):not([value*="SUBSCRIBE"]):not([name*="subscribe"]):not([name*="Subscribe"]):not(.bg-black.text-white) {
        /* Only apply to buttons without explicit light backgrounds */
        color: #000000 !important;
      }
      
      html[data-contrast-mode="${mode}"] button:not(.ada-contrast-toggle):not(.ada-global-pause-toggle):not(.ada-video-control):not([value*="Subscribe"]):not([value*="SUBSCRIBE"]):not([name*="subscribe"]):not([name*="Subscribe"]):not(.bg-black.text-white)[class*="bg-black"],
      html[data-contrast-mode="${mode}"] button:not(.ada-contrast-toggle):not(.ada-global-pause-toggle):not(.ada-video-control):not([value*="Subscribe"]):not([value*="SUBSCRIBE"]):not([name*="subscribe"]):not([name*="Subscribe"]):not(.bg-black.text-white)[style*="background: #000"],
      html[data-contrast-mode="${mode}"] button:not(.ada-contrast-toggle):not(.ada-global-pause-toggle):not(.ada-video-control):not([value*="Subscribe"]):not([value*="SUBSCRIBE"]):not([name*="subscribe"]):not([name*="Subscribe"]):not(.bg-black.text-white)[style*="background-color: #000"] {
        color: #ffffff !important;
      }
      
      html[data-contrast-mode="${mode}"] button:not(.ada-contrast-toggle):not(.ada-global-pause-toggle):not(.ada-video-control):not([value*="Subscribe"]):not([value*="SUBSCRIBE"]):not([name*="subscribe"]):not([name*="Subscribe"]):not(.bg-black.text-white)[class*="bg-white"],
      html[data-contrast-mode="${mode}"] button:not(.ada-contrast-toggle):not(.ada-global-pause-toggle):not(.ada-video-control):not([value*="Subscribe"]):not([value*="SUBSCRIBE"]):not([name*="subscribe"]):not([name*="Subscribe"]):not(.bg-black.text-white)[style*="background: #fff"],
      html[data-contrast-mode="${mode}"] button:not(.ada-contrast-toggle):not(.ada-global-pause-toggle):not(.ada-video-control):not([value*="Subscribe"]):not([value*="SUBSCRIBE"]):not([name*="subscribe"]):not([name*="Subscribe"]):not(.bg-black.text-white)[style*="background-color: #fff"] {
        color: #000000 !important;
      }
      
      /* Video controls - ensure text/icons are white when background is black */
      html[data-contrast-mode="${mode}"] .ada-video-control,
      html[data-contrast-mode="${mode}"] .ada-video-control *,
      html[data-contrast-mode="${mode}"] .ada-video-control span,
      html[data-contrast-mode="${mode}"] .ada-video-control .ada-video-icon-play,
      html[data-contrast-mode="${mode}"] .ada-video-control .ada-video-icon-pause,
      html[data-contrast-mode="${mode}"] .ada-video-control .ada-video-icon-mute,
      html[data-contrast-mode="${mode}"] .ada-video-control .ada-video-icon-unmute,
      html[data-contrast-mode="${mode}"] .ada-video-control .ada-video-icon-captions-on,
      html[data-contrast-mode="${mode}"] .ada-video-control .ada-video-icon-captions-off,
      html[data-contrast-mode="${mode}"] .ada-video-control .ada-video-icon-transcript-on,
      html[data-contrast-mode="${mode}"] .ada-video-control .ada-video-icon-transcript-off,
      html[data-contrast-mode="${mode}"] .ada-video-control .ada-video-icon-fullscreen {
        color: #ffffff !important;
        background-color: #000000 !important;
        background: #000000 !important;
      }
      
      html[data-contrast-mode="${mode}"] .text-black,
      html[data-contrast-mode="${mode}"] .text-white,
      html[data-contrast-mode="${mode}"] .text-red,
      html[data-contrast-mode="${mode}"] .text-blue,
      html[data-contrast-mode="${mode}"] [class*="text-"] {
        color: #000000 !important;
      }
      
      /* Hamburger menu icon - ensure it's black in high contrast mode when header is white */
      html[data-contrast-mode="${mode}"] .header-menu-toggle svg,
      html[data-contrast-mode="${mode}"] .header-menu-toggle button svg,
      html[data-contrast-mode="${mode}"] button.js-open-mm svg,
      html[data-contrast-mode="${mode}"] .header-menu-toggle svg g#hamburger,
      html[data-contrast-mode="${mode}"] .header-menu-toggle button svg g#hamburger,
      html[data-contrast-mode="${mode}"] button.js-open-mm svg g#hamburger,
      html[data-contrast-mode="${mode}"] .header-menu-toggle svg path,
      html[data-contrast-mode="${mode}"] .header-menu-toggle button svg path,
      html[data-contrast-mode="${mode}"] button.js-open-mm svg path {
        color: #000000 !important;
        stroke: #000000 !important;
        fill: #000000 !important;
      }
      
      /* Footer accessibility buttons - better visual states following best practices */
      /* CRITICAL: Override ALL hiding rules from ada-contrast-control.css and ada-global-pause.css */
      /* These buttons should ALWAYS be visible in the footer */
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle,
      html[data-contrast-mode="${mode}"] [data-footer-pause-target] .ada-global-pause-toggle,
      html[data-contrast-mode="${mode}"] .ada-footer-control.ada-contrast-toggle,
      html[data-contrast-mode="${mode}"] .ada-footer-control.ada-global-pause-toggle {
        opacity: 1 !important;
        visibility: visible !important;
        display: inline-flex !important;
        width: auto !important;
        height: auto !important;
        min-width: fit-content !important;
        max-width: none !important;
        overflow: visible !important;
        clip: auto !important;
        clip-path: none !important;
        position: relative !important;
        left: auto !important;
        margin: 0 8px 0 0 !important;
        padding: 8px 12px !important;
        background-color: #ffffff !important;
        border: 2px solid #000000 !important;
        color: #000000 !important;
        font-size: 12px !important;
        font-weight: 500 !important;
        line-height: 1.4 !important;
        cursor: pointer !important;
        text-decoration: none !important;
        border-radius: 4px !important;
      }
      
      /* Default state: white background, black text, black border - ALWAYS visible */
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle:not(.active):not([aria-pressed="true"]),
      html[data-contrast-mode="${mode}"] [data-footer-pause-target] .ada-global-pause-toggle:not(.active):not(.is-paused):not([aria-pressed="true"]) {
        background-color: #ffffff !important;
        border: 2px solid #000000 !important;
        color: #000000 !important;
      }
      
      /* Ensure default state text and icons are visible and black */
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle:not(.active):not([aria-pressed="true"]) *,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle:not(.active):not([aria-pressed="true"]) .ada-contrast-toggle-text,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle:not(.active):not([aria-pressed="true"]) .ada-contrast-toggle-icon,
      html[data-contrast-mode="${mode}"] [data-footer-pause-target] .ada-global-pause-toggle:not(.active):not(.is-paused):not([aria-pressed="true"]) *,
      html[data-contrast-mode="${mode}"] [data-footer-pause-target] .ada-global-pause-toggle:not(.active):not(.is-paused):not([aria-pressed="true"]) .ada-pause-text,
      html[data-contrast-mode="${mode}"] [data-footer-pause-target] .ada-global-pause-toggle:not(.active):not(.is-paused):not([aria-pressed="true"]) .ada-pause-icon {
        color: #000000 !important;
        opacity: 1 !important;
        visibility: visible !important;
        display: inline-block !important;
      }
      
      /* Default state SVG icons - black */
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle:not(.active):not([aria-pressed="true"]) svg,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle:not(.active):not([aria-pressed="true"]) .ada-contrast-toggle-icon svg,
      html[data-contrast-mode="${mode}"] [data-footer-pause-target] .ada-global-pause-toggle:not(.active):not(.is-paused):not([aria-pressed="true"]) svg,
      html[data-contrast-mode="${mode}"] [data-footer-pause-target] .ada-global-pause-toggle:not(.active):not(.is-paused):not([aria-pressed="true"]) .ada-pause-icon svg {
        fill: #000000 !important;
        color: #000000 !important;
        stroke: #000000 !important;
        opacity: 1 !important;
        visibility: visible !important;
      }
      
      /* Active state: High Contrast button - inverted (black bg, white text) when ON - BEST PRACTICE */
      /* When engaged: Black background with white text/icon for clear visual indication */
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle.active,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle[aria-pressed="true"] {
        background-color: #000000 !important;
        border: 2px solid #000000 !important;
        color: #ffffff !important;
        opacity: 1 !important;
        visibility: visible !important;
        display: inline-flex !important;
      }
      
      /* CRITICAL: Active state text/icons for contrast button - MUST be white for visibility */
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle.active *,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle[aria-pressed="true"] *,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle.active .ada-contrast-toggle-text,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle.active .ada-contrast-toggle-icon,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle[aria-pressed="true"] .ada-contrast-toggle-text,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle[aria-pressed="true"] .ada-contrast-toggle-icon,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle.active span,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle[aria-pressed="true"] span {
        color: #ffffff !important;
        opacity: 1 !important;
        visibility: visible !important;
        display: inline-block !important;
      }
      
      /* CRITICAL: Active state SVG icons for contrast button - MUST be white */
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle.active svg,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle[aria-pressed="true"] svg,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle.active .ada-contrast-toggle-icon svg,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle[aria-pressed="true"] .ada-contrast-toggle-icon svg,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle.active svg *,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle[aria-pressed="true"] svg *,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle.active svg path,
      html[data-contrast-mode="${mode}"] [data-footer-contrast-target] .ada-contrast-toggle[aria-pressed="true"] svg path {
        fill: #ffffff !important;
        color: #ffffff !important;
        stroke: #ffffff !important;
        opacity: 1 !important;
        visibility: visible !important;
      }
      
      /* Active state: Pause button - thicker border and different background when paused */
      html[data-contrast-mode="${mode}"] [data-footer-pause-target] .ada-global-pause-toggle.active,
      html[data-contrast-mode="${mode}"] [data-footer-pause-target] .ada-global-pause-toggle.is-paused,
      html[data-contrast-mode="${mode}"] [data-footer-pause-target] .ada-global-pause-toggle[aria-pressed="true"] {
        background-color: #f0f0f0 !important;
        border: 3px solid #000000 !important;
        color: #000000 !important;
        opacity: 1 !important;
        visibility: visible !important;
        display: inline-flex !important;
        font-weight: bold !important;
        /* CRITICAL: Prevent size changes when toggling - maintain consistent width */
        width: auto !important;
        min-width: fit-content !important;
        max-width: none !important;
        box-sizing: border-box !important;
        overflow: hidden !important;
        white-space: nowrap !important;
        text-overflow: ellipsis !important;
      }
      
      /* Footer pause button - ensure text doesn't overflow when toggling */
      html[data-contrast-mode="${mode}"] [data-footer-pause-target] .ada-global-pause-toggle {
        box-sizing: border-box !important;
        overflow: hidden !important;
        white-space: nowrap !important;
        text-overflow: ellipsis !important;
      }
      
      html[data-contrast-mode="${mode}"] [data-footer-pause-target] .ada-global-pause-toggle .ada-pause-text {
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        white-space: nowrap !important;
        max-width: 100% !important;
      }
      
      /* Active state text/icons for pause button - black */
      html[data-contrast-mode="${mode}"] [data-footer-pause-target] .ada-global-pause-toggle.active *,
      html[data-contrast-mode="${mode}"] [data-footer-pause-target] .ada-global-pause-toggle.is-paused *,
      html[data-contrast-mode="${mode}"] [data-footer-pause-target] .ada-global-pause-toggle[aria-pressed="true"] *,
      html[data-contrast-mode="${mode}"] [data-footer-pause-target] .ada-global-pause-toggle.active .ada-pause-text,
      html[data-contrast-mode="${mode}"] [data-footer-pause-target] .ada-global-pause-toggle.active .ada-pause-icon,
      html[data-contrast-mode="${mode}"] [data-footer-pause-target] .ada-global-pause-toggle.is-paused .ada-pause-text,
      html[data-contrast-mode="${mode}"] [data-footer-pause-target] .ada-global-pause-toggle.is-paused .ada-pause-icon {
        color: #000000 !important;
        opacity: 1 !important;
        visibility: visible !important;
      }
      
      /* Active state SVG icons for pause button - black */
      html[data-contrast-mode="${mode}"] [data-footer-pause-target] .ada-global-pause-toggle.active svg,
      html[data-contrast-mode="${mode}"] [data-footer-pause-target] .ada-global-pause-toggle.is-paused svg,
      html[data-contrast-mode="${mode}"] [data-footer-pause-target] .ada-global-pause-toggle[aria-pressed="true"] svg,
      html[data-contrast-mode="${mode}"] [data-footer-pause-target] .ada-global-pause-toggle.active .ada-pause-icon svg,
      html[data-contrast-mode="${mode}"] [data-footer-pause-target] .ada-global-pause-toggle.is-paused .ada-pause-icon svg {
        fill: #000000 !important;
        color: #000000 !important;
        stroke: #000000 !important;
        opacity: 1 !important;
        visibility: visible !important;
      }
      
      /* Mobile menu accessibility buttons - keep existing styling */
      html[data-contrast-mode="${mode}"] .mm .ada-contrast-toggle,
      html[data-contrast-mode="${mode}"] .mm .ada-global-pause-toggle,
      html[data-contrast-mode="${mode}"] .mm [data-mm-contrast-target] button,
      html[data-contrast-mode="${mode}"] .mm [data-mm-pause-target] button {
        background-color: #ffffff !important;
        border: 2px solid #000000 !important;
        color: #000000 !important;
        opacity: 1 !important;
        visibility: visible !important;
        display: flex !important;
      }
      
      /* Ensure button text and icons are visible */
      html[data-contrast-mode="${mode}"] .mm .ada-contrast-toggle *,
      html[data-contrast-mode="${mode}"] .mm .ada-global-pause-toggle *,
      html[data-contrast-mode="${mode}"] .mm .ada-contrast-toggle-text,
      html[data-contrast-mode="${mode}"] .mm .ada-pause-text,
      html[data-contrast-mode="${mode}"] .mm .ada-contrast-toggle-icon,
      html[data-contrast-mode="${mode}"] .mm .ada-pause-icon {
        color: #000000 !important;
        opacity: 1 !important;
        visibility: visible !important;
        display: inline-block !important;
      }
      
      /* Ensure SVG icons in buttons are visible */
      /* Default state - black icons */
      html[data-contrast-mode="${mode}"] .mm .ada-contrast-toggle:not(.active):not([aria-pressed="true"]) svg,
      html[data-contrast-mode="${mode}"] .mm .ada-contrast-toggle:not(.active):not([aria-pressed="true"]) .ada-contrast-toggle-icon svg,
      html[data-contrast-mode="${mode}"] .mm .ada-global-pause-toggle svg,
      html[data-contrast-mode="${mode}"] .mm .ada-pause-icon svg {
        fill: #000000 !important;
        color: #000000 !important;
        opacity: 1 !important;
        visibility: visible !important;
      }
      
      /* Active state - white icons for contrast button */
      html[data-contrast-mode="${mode}"] .mm .ada-contrast-toggle.active svg,
      html[data-contrast-mode="${mode}"] .mm .ada-contrast-toggle[aria-pressed="true"] svg,
      html[data-contrast-mode="${mode}"] .mm .ada-contrast-toggle.active .ada-contrast-toggle-icon svg,
      html[data-contrast-mode="${mode}"] .mm .ada-contrast-toggle[aria-pressed="true"] .ada-contrast-toggle-icon svg {
        fill: #ffffff !important;
        color: #ffffff !important;
        opacity: 1 !important;
        visibility: visible !important;
      }
      
      /* Active state - contrast button should be inverted (black bg, white text) when ON */
      html[data-contrast-mode="${mode}"] .mm .ada-contrast-toggle.active,
      html[data-contrast-mode="${mode}"] .mm .ada-contrast-toggle[aria-pressed="true"] {
        background-color: #000000 !important; /* Black background when active */
        color: #ffffff !important; /* White text when active */
        border-color: #000000 !important;
        opacity: 1 !important;
        visibility: visible !important;
      }
      
      /* Active state text and icons for contrast button - white when active */
      html[data-contrast-mode="${mode}"] .mm .ada-contrast-toggle.active *,
      html[data-contrast-mode="${mode}"] .mm .ada-contrast-toggle[aria-pressed="true"] * {
        color: #ffffff !important; /* White text/icons when active */
        opacity: 1 !important;
        visibility: visible !important;
      }
      
      /* Pause button active state - keep white background */
      html[data-contrast-mode="${mode}"] .mm .ada-global-pause-toggle.active,
      html[data-contrast-mode="${mode}"] .mm .ada-global-pause-toggle.is-paused,
      html[data-contrast-mode="${mode}"] .mm .ada-global-pause-toggle[aria-pressed="true"] {
        background-color: #ffffff !important;
        color: #000000 !important;
        border-color: #000000 !important;
        opacity: 1 !important;
        visibility: visible !important;
      }
      
      /* Pause button active state text and icons - black */
      html[data-contrast-mode="${mode}"] .mm .ada-global-pause-toggle.active *,
      html[data-contrast-mode="${mode}"] .mm .ada-global-pause-toggle.is-paused *,
      html[data-contrast-mode="${mode}"] .mm .ada-global-pause-toggle[aria-pressed="true"] * {
        color: #000000 !important;
        opacity: 1 !important;
        visibility: visible !important;
      }
      
      /* Carousel navigation buttons - black background with white arrows */
      html[data-contrast-mode="${mode}"] .ctrl-bar button,
      html[data-contrast-mode="${mode}"] .ctrl-prev,
      html[data-contrast-mode="${mode}"] .ctrl-next,
      html[data-contrast-mode="${mode}"] .ctrl-playpause,
      html[data-contrast-mode="${mode}"] .ch-nav-prev,
      html[data-contrast-mode="${mode}"] .ch-nav-next {
        background: #000000 !important;
        background-color: #000000 !important;
        color: #ffffff !important;
        border-color: #ffffff !important;
      }
      
      html[data-contrast-mode="${mode}"] .ctrl-bar button svg,
      html[data-contrast-mode="${mode}"] .ctrl-prev svg,
      html[data-contrast-mode="${mode}"] .ctrl-next svg,
      html[data-contrast-mode="${mode}"] .ctrl-playpause svg,
      html[data-contrast-mode="${mode}"] .ch-nav-prev svg,
      html[data-contrast-mode="${mode}"] .ch-nav-next svg {
        stroke: #ffffff !important;
        fill: #ffffff !important;
        color: #ffffff !important;
      }
      
      /* Ensure all SVG paths and elements inside carousel buttons are white */
      html[data-contrast-mode="${mode}"] .ctrl-bar button svg *,
      html[data-contrast-mode="${mode}"] .ctrl-prev svg *,
      html[data-contrast-mode="${mode}"] .ctrl-next svg *,
      html[data-contrast-mode="${mode}"] .ctrl-playpause svg *,
      html[data-contrast-mode="${mode}"] .ch-nav-prev svg *,
      html[data-contrast-mode="${mode}"] .ch-nav-next svg * {
        stroke: #ffffff !important;
        fill: #ffffff !important;
        color: #ffffff !important;
      }
      
      /* Swiper.js carousel buttons - ensure arrows are visible */
      html[data-contrast-mode="${mode}"] .swiper-button-next,
      html[data-contrast-mode="${mode}"] .swiper-button-prev,
      html[data-contrast-mode="${mode}"] .swiper-button-next::after,
      html[data-contrast-mode="${mode}"] .swiper-button-prev::after {
        color: #ffffff !important;
        background: #000000 !important;
        background-color: #000000 !important;
      }
      
      html[data-contrast-mode="${mode}"] .swiper-button-next svg,
      html[data-contrast-mode="${mode}"] .swiper-button-prev svg {
        stroke: #ffffff !important;
        fill: #ffffff !important;
        color: #ffffff !important;
        filter: none !important;
      }
      
      html[data-contrast-mode="${mode}"] .swiper-button-next svg *,
      html[data-contrast-mode="${mode}"] .swiper-button-prev svg * {
        stroke: #ffffff !important;
        fill: #ffffff !important;
        color: #ffffff !important;
      }
      
      /* Chat widget (Zendesk) - only style the button, not containers */
      html[data-contrast-mode="${mode}"] button[aria-label*="chat"],
      html[data-contrast-mode="${mode}"] button[aria-label*="Chat"] {
        background-color: #ffffff !important;
        background: #ffffff !important;
        color: #000000 !important;
        border-color: #000000 !important;
      }
      
      /* Chat widget button icons and text - black (only button children) */
      html[data-contrast-mode="${mode}"] button[aria-label*="chat"] *,
      html[data-contrast-mode="${mode}"] button[aria-label*="Chat"] * {
        color: #000000 !important;
        fill: #000000 !important;
        stroke: #000000 !important;
      }
      
      /* AudioEye widget styling removed - using JavaScript only for precise targeting */
      
      /* Navigation bars - black background with white text for WCAG compliance */
      /* CRITICAL: Override inline styles and CSS variables with maximum specificity */
      html[data-contrast-mode="${mode}"] .text-bar,
      html[data-contrast-mode="${mode}"] .text-bar[style],
      html[data-contrast-mode="${mode}"] .ch-bar,
      html[data-contrast-mode="${mode}"] .ch-bar[style],
      html[data-contrast-mode="${mode}"] .asg-nav-bar,
      html[data-contrast-mode="${mode}"] .asg-nav-bar[style] {
        background-color: #000000 !important;
        background: #000000 !important;
        backdrop-filter: none !important;
        -webkit-backdrop-filter: none !important;
        /* Override CSS variables */
        --bar-txt-color: #ffffff !important;
        --nav-text-color: #ffffff !important;
      }
      
      /* All text and links inside nav bars - white text, override CSS variables */
      html[data-contrast-mode="${mode}"] .text-bar,
      html[data-contrast-mode="${mode}"] .text-bar *,
      html[data-contrast-mode="${mode}"] .text-bar-inner,
      html[data-contrast-mode="${mode}"] .text-bar-inner *,
      html[data-contrast-mode="${mode}"] .text-bar-link,
      html[data-contrast-mode="${mode}"] .text-bar-text,
      html[data-contrast-mode="${mode}"] .ch-bar,
      html[data-contrast-mode="${mode}"] .ch-bar *,
      html[data-contrast-mode="${mode}"] .ch-bar-inner,
      html[data-contrast-mode="${mode}"] .ch-bar-inner *,
      html[data-contrast-mode="${mode}"] .ch-link,
      html[data-contrast-mode="${mode}"] .ch-link *,
      html[data-contrast-mode="${mode}"] .ch-text,
      html[data-contrast-mode="${mode}"] .ch-slot,
      html[data-contrast-mode="${mode}"] .ch-slot *,
      html[data-contrast-mode="${mode}"] .asg-nav-bar,
      html[data-contrast-mode="${mode}"] .asg-nav-bar *,
      html[data-contrast-mode="${mode}"] .asg-nav-link,
      html[data-contrast-mode="${mode}"] .asg-nav-slot,
      html[data-contrast-mode="${mode}"] .asg-nav-slot * {
        color: #ffffff !important;
        background-color: transparent !important;
        background: transparent !important;
        /* Override any CSS variable usage */
        --bar-txt-color: #ffffff !important;
        --nav-text-color: #ffffff !important;
      }
      
      /* Countdown clock - preserve original padding, only apply black background to text/numbers */
      html[data-contrast-mode="${mode}"] .cd-wrap {
        background-color: transparent !important;
        background: transparent !important;
        /* CRITICAL: Preserve original padding - don't override */
        padding-top: var(--cd-pad-t) !important;
        padding-bottom: var(--cd-pad-b) !important;
        margin-top: inherit !important;
        margin-bottom: inherit !important;
      }
      
      /* Mobile: preserve mobile padding */
      @media (max-width: 749px) {
        html[data-contrast-mode="${mode}"] .cd-wrap {
          padding-top: var(--cd-pad-t-m) !important;
          padding-bottom: var(--cd-pad-b-m) !important;
        }
      }
      
      html[data-contrast-mode="${mode}"] .cd-grid,
      html[data-contrast-mode="${mode}"] .cd-cell {
        background-color: transparent !important;
        background: transparent !important;
      }
      
      /* Only apply black background to the actual text elements with padding */
      html[data-contrast-mode="${mode}"] .cd-value,
      html[data-contrast-mode="${mode}"] .cd-label {
        color: #ffffff !important;
        background-color: #000000 !important;
        background: #000000 !important;
        padding: 4px 8px !important;
        display: inline-block !important;
      }
      
      /* Product size buttons */
      html[data-contrast-mode="${mode}"] .size-button {
        border-color: #000000 !important;
        background: #ffffff !important;
        background-color: #ffffff !important;
        color: #000000 !important;
      }

      html[data-contrast-mode="${mode}"] .size-button[data-checked="true"],
      html[data-contrast-mode="${mode}"] .size-button.border-green-500 {
        border-color: #000000 !important;
        background: #000000 !important;
        background-color: #000000 !important;
        color: #ffffff !important;
      }
      
      html[data-contrast-mode="${mode}"] .size-button:focus-visible,
      html[data-contrast-mode="${mode}"] input[data-option-selector]:focus-visible + label {
        outline: 3px solid #ffffff !important;
        outline-offset: 2px;
      }

      html[data-contrast-mode="${mode}"] .color-swatch-thumbnail {
        border-color: #000000 !important;
        box-shadow: none !important;
      }

      html[data-contrast-mode="${mode}"] .color-swatch-wrapper input:checked + label.color-swatch-thumbnail {
        border-color: #ffffff !important;
        box-shadow: 0 0 0 2px #ffffff !important;
      }

      html[data-contrast-mode="${mode}"] .color-swatch-wrapper input:focus-visible + label.color-swatch-thumbnail {
        outline: 3px solid #ffffff !important;
        outline-offset: 2px;
      }
      
      /* Cookie consent pop-ups - white background with black text */
      html[data-contrast-mode="${mode}"] [id*="cookie"],
      html[data-contrast-mode="${mode}"] [class*="cookie"],
      html[data-contrast-mode="${mode}"] [id*="Cookie"],
      html[data-contrast-mode="${mode}"] [class*="Cookie"],
      html[data-contrast-mode="${mode}"] [id*="consent"],
      html[data-contrast-mode="${mode}"] [class*="consent"],
      html[data-contrast-mode="${mode}"] [id*="gdpr"],
      html[data-contrast-mode="${mode}"] [class*="gdpr"] {
        background-color: #ffffff !important;
        background: #ffffff !important;
        color: #000000 !important;
        border-color: #000000 !important;
      }
      
      /* Cookie pop-up text - black, but exclude buttons */
      html[data-contrast-mode="${mode}"] [id*="cookie"] p:not(button p),
      html[data-contrast-mode="${mode}"] [class*="cookie"] p:not(button p),
      html[data-contrast-mode="${mode}"] [id*="Cookie"] p:not(button p),
      html[data-contrast-mode="${mode}"] [class*="Cookie"] p:not(button p),
      html[data-contrast-mode="${mode}"] [id*="consent"] p:not(button p),
      html[data-contrast-mode="${mode}"] [class*="consent"] p:not(button p),
      html[data-contrast-mode="${mode}"] [id*="gdpr"] p:not(button p),
      html[data-contrast-mode="${mode}"] [class*="gdpr"] p:not(button p),
      html[data-contrast-mode="${mode}"] [id*="cookie"] div:not(button):not(button div),
      html[data-contrast-mode="${mode}"] [class*="cookie"] div:not(button):not(button div),
      html[data-contrast-mode="${mode}"] [id*="Cookie"] div:not(button):not(button div),
      html[data-contrast-mode="${mode}"] [class*="Cookie"] div:not(button):not(button div),
      html[data-contrast-mode="${mode}"] [id*="consent"] div:not(button):not(button div),
      html[data-contrast-mode="${mode}"] [class*="consent"] div:not(button):not(button div),
      html[data-contrast-mode="${mode}"] [id*="gdpr"] div:not(button):not(button div),
      html[data-contrast-mode="${mode}"] [class*="gdpr"] div:not(button):not(button div),
      html[data-contrast-mode="${mode}"] [id*="cookie"] span:not(button span),
      html[data-contrast-mode="${mode}"] [class*="cookie"] span:not(button span),
      html[data-contrast-mode="${mode}"] [id*="Cookie"] span:not(button span),
      html[data-contrast-mode="${mode}"] [class*="Cookie"] span:not(button span),
      html[data-contrast-mode="${mode}"] [id*="consent"] span:not(button span),
      html[data-contrast-mode="${mode}"] [class*="consent"] span:not(button span),
      html[data-contrast-mode="${mode}"] [id*="gdpr"] span:not(button span),
      html[data-contrast-mode="${mode}"] [class*="gdpr"] span:not(button span) {
        color: #000000 !important;
      }
      
      /* Cookie pop-up links - black text (not blue) */
      html[data-contrast-mode="${mode}"] [id*="cookie"] a:not(button):not([class*="button"]),
      html[data-contrast-mode="${mode}"] [class*="cookie"] a:not(button):not([class*="button"]),
      html[data-contrast-mode="${mode}"] [id*="Cookie"] a:not(button):not([class*="button"]),
      html[data-contrast-mode="${mode}"] [class*="Cookie"] a:not(button):not([class*="button"]),
      html[data-contrast-mode="${mode}"] [id*="consent"] a:not(button):not([class*="button"]),
      html[data-contrast-mode="${mode}"] [class*="consent"] a:not(button):not([class*="button"]),
      html[data-contrast-mode="${mode}"] [id*="gdpr"] a:not(button):not([class*="button"]),
      html[data-contrast-mode="${mode}"] [class*="gdpr"] a:not(button):not([class*="button"]) {
        color: #000000 !important;
        text-decoration: underline !important;
      }
      
      /* Cookie consent buttons - WHITE background with BLACK text for better visibility */
      html[data-contrast-mode="${mode}"] [id*="cookie"] button,
      html[data-contrast-mode="${mode}"] [class*="cookie"] button,
      html[data-contrast-mode="${mode}"] [id*="Cookie"] button,
      html[data-contrast-mode="${mode}"] [class*="Cookie"] button,
      html[data-contrast-mode="${mode}"] [id*="consent"] button,
      html[data-contrast-mode="${mode}"] [class*="consent"] button,
      html[data-contrast-mode="${mode}"] [id*="gdpr"] button,
      html[data-contrast-mode="${mode}"] [class*="gdpr"] button,
      html[data-contrast-mode="${mode}"] [id*="cookie"] a[class*="button"],
      html[data-contrast-mode="${mode}"] [class*="cookie"] a[class*="button"],
      html[data-contrast-mode="${mode}"] [id*="Cookie"] a[class*="button"],
      html[data-contrast-mode="${mode}"] [class*="Cookie"] a[class*="button"],
      html[data-contrast-mode="${mode}"] [id*="consent"] a[class*="button"],
      html[data-contrast-mode="${mode}"] [class*="consent"] a[class*="button"],
      html[data-contrast-mode="${mode}"] [id*="gdpr"] a[class*="button"],
      html[data-contrast-mode="${mode}"] [class*="gdpr"] a[class*="button"] {
        background-color: #ffffff !important;
        background: #ffffff !important;
        color: #000000 !important;
        border-color: #000000 !important;
      }
      
      /* Ensure button text and children are BLACK - Maximum specificity */
      html[data-contrast-mode="${mode}"] [id*="cookie"] button,
      html[data-contrast-mode="${mode}"] [class*="cookie"] button,
      html[data-contrast-mode="${mode}"] [id*="Cookie"] button,
      html[data-contrast-mode="${mode}"] [class*="Cookie"] button,
      html[data-contrast-mode="${mode}"] [id*="consent"] button,
      html[data-contrast-mode="${mode}"] [class*="consent"] button,
      html[data-contrast-mode="${mode}"] [id*="gdpr"] button,
      html[data-contrast-mode="${mode}"] [class*="gdpr"] button,
      html[data-contrast-mode="${mode}"] [id*="cookie"] a[class*="button"],
      html[data-contrast-mode="${mode}"] [class*="cookie"] a[class*="button"],
      html[data-contrast-mode="${mode}"] [id*="Cookie"] a[class*="button"],
      html[data-contrast-mode="${mode}"] [class*="Cookie"] a[class*="button"],
      html[data-contrast-mode="${mode}"] [id*="consent"] a[class*="button"],
      html[data-contrast-mode="${mode}"] [class*="consent"] a[class*="button"],
      html[data-contrast-mode="${mode}"] [id*="gdpr"] a[class*="button"],
      html[data-contrast-mode="${mode}"] [class*="gdpr"] a[class*="button"] {
        color: #000000 !important;
      }
      
      /* Target specific text elements inside cookie buttons - BLACK text */
      html[data-contrast-mode="${mode}"] [id*="cookie"] button span,
      html[data-contrast-mode="${mode}"] [class*="cookie"] button span,
      html[data-contrast-mode="${mode}"] [id*="Cookie"] button span,
      html[data-contrast-mode="${mode}"] [class*="Cookie"] button span,
      html[data-contrast-mode="${mode}"] [id*="consent"] button span,
      html[data-contrast-mode="${mode}"] [class*="consent"] button span,
      html[data-contrast-mode="${mode}"] [id*="gdpr"] button span,
      html[data-contrast-mode="${mode}"] [class*="gdpr"] button span,
      html[data-contrast-mode="${mode}"] [id*="cookie"] button div,
      html[data-contrast-mode="${mode}"] [class*="cookie"] button div,
      html[data-contrast-mode="${mode}"] [id*="Cookie"] button div,
      html[data-contrast-mode="${mode}"] [class*="Cookie"] button div,
      html[data-contrast-mode="${mode}"] [id*="consent"] button div,
      html[data-contrast-mode="${mode}"] [class*="consent"] button div,
      html[data-contrast-mode="${mode}"] [id*="gdpr"] button div,
      html[data-contrast-mode="${mode}"] [class*="gdpr"] button div,
      html[data-contrast-mode="${mode}"] [id*="cookie"] button p,
      html[data-contrast-mode="${mode}"] [class*="cookie"] button p,
      html[data-contrast-mode="${mode}"] [id*="Cookie"] button p,
      html[data-contrast-mode="${mode}"] [class*="Cookie"] button p,
      html[data-contrast-mode="${mode}"] [id*="consent"] button p,
      html[data-contrast-mode="${mode}"] [class*="consent"] button p,
      html[data-contrast-mode="${mode}"] [id*="gdpr"] button p,
      html[data-contrast-mode="${mode}"] [class*="gdpr"] button p,
      html[data-contrast-mode="${mode}"] [id*="cookie"] a[class*="button"] span,
      html[data-contrast-mode="${mode}"] [class*="cookie"] a[class*="button"] span,
      html[data-contrast-mode="${mode}"] [id*="cookie"] a[class*="button"] div,
      html[data-contrast-mode="${mode}"] [class*="cookie"] a[class*="button"] div,
      html[data-contrast-mode="${mode}"] [id*="cookie"] a[class*="button"] p,
      html[data-contrast-mode="${mode}"] [class*="cookie"] a[class*="button"] p,
      html[data-contrast-mode="${mode}"] [id*="cookie"] button *,
      html[data-contrast-mode="${mode}"] [class*="cookie"] button *,
      html[data-contrast-mode="${mode}"] [id*="Cookie"] button *,
      html[data-contrast-mode="${mode}"] [class*="Cookie"] button *,
      html[data-contrast-mode="${mode}"] [id*="consent"] button *,
      html[data-contrast-mode="${mode}"] [class*="consent"] button *,
      html[data-contrast-mode="${mode}"] [id*="gdpr"] button *,
      html[data-contrast-mode="${mode}"] [class*="gdpr"] button *,
      html[data-contrast-mode="${mode}"] [id*="cookie"] a[class*="button"] *,
      html[data-contrast-mode="${mode}"] [class*="cookie"] a[class*="button"] * {
        color: #000000 !important;
      }
      
      html[data-contrast-mode="${mode}"] [id*="cookie"] button:hover,
      html[data-contrast-mode="${mode}"] [class*="cookie"] button:hover,
      html[data-contrast-mode="${mode}"] [id*="Cookie"] button:hover,
      html[data-contrast-mode="${mode}"] [class*="Cookie"] button:hover,
      html[data-contrast-mode="${mode}"] [id*="consent"] button:hover,
      html[data-contrast-mode="${mode}"] [class*="consent"] button:hover,
      html[data-contrast-mode="${mode}"] [id*="gdpr"] button:hover,
      html[data-contrast-mode="${mode}"] [class*="gdpr"] button:hover {
        background-color: #e0e0e0 !important;
        background: #e0e0e0 !important;
        color: #000000 !important;
      }
    `;
    
    document.head.appendChild(style);
  }

  // Remove contrast mode
  function removeContrastMode() {
    document.documentElement.removeAttribute('data-contrast-mode');
    
    // Remove injected styles
    const existingStyle = document.getElementById('ada-contrast-injected');
    if (existingStyle) {
      existingStyle.remove();
    }
    
    // Restore original grey filter on minus signs and nav bar styles
    setTimeout(function() {
      const minusSigns = document.querySelectorAll('.mm img[src*="Minus.svg"].mm-child-minus, .mm .sub-menu img[src*="Minus.svg"], .mm ul li img[src*="Minus.svg"]');
      minusSigns.forEach(function(img) {
        if (img.classList.contains('mm-child-minus') || img.closest('.sub-menu') || img.closest('ul li')) {
          img.style.setProperty('filter', 'brightness(0) saturate(100%) invert(65%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(95%) contrast(90%)', 'important');
        }
      });
      
      // Restore nav bar inline styles (remove our overrides)
      const navBars = document.querySelectorAll('.text-bar, .ch-bar, .asg-nav-bar');
      navBars.forEach(function(navBar) {
        navBar.style.removeProperty('background-color');
        navBar.style.removeProperty('background');
        navBar.style.removeProperty('backdrop-filter');
        navBar.style.removeProperty('-webkit-backdrop-filter');
        navBar.style.removeProperty('--bar-txt-color');
        navBar.style.removeProperty('--nav-text-color');
        
        const textElements = navBar.querySelectorAll('*');
        textElements.forEach(function(el) {
          el.style.removeProperty('color');
          el.style.removeProperty('--bar-txt-color');
          el.style.removeProperty('--nav-text-color');
        });
      });
    }, 100);
  }

  function updateAllContrastButtons(mode) {
    contrastToggleButtons.forEach(function(button) {
      updateToggleButton(button, mode);
    });
  }

  // Update toggle button state
  function updateToggleButton(button, mode) {
    const isActive = mode !== MODES.OFF;
    button.setAttribute('aria-pressed', isActive.toString());
    button.classList.toggle('active', isActive);
    
    // CRITICAL: Update button colors based on active state using setProperty with important flag
    const isMobileMenu = button.dataset && button.dataset.context === 'mobile-menu';
    const isFooter = button.dataset && button.dataset.context === 'footer';
    
    if (isMobileMenu) {
      if (isActive) {
        // Active state: black background, white text
        button.style.setProperty('background-color', '#000000', 'important');
        button.style.setProperty('color', '#ffffff', 'important');
        button.style.setProperty('border-color', '#000000', 'important');
      } else {
        // Inactive state: white background, black text
        button.style.setProperty('background-color', '#ffffff', 'important');
        button.style.setProperty('color', '#000000', 'important');
        button.style.setProperty('border-color', '#000000', 'important');
      }
    } else if (isFooter) {
      // Footer buttons: Best practice - inverted colors when active
      if (isActive) {
        // Active state: black background, white text/icons (BEST PRACTICE)
        button.style.setProperty('background-color', '#000000', 'important');
        button.style.setProperty('color', '#ffffff', 'important');
        button.style.setProperty('border-color', '#000000', 'important');
        
        // CRITICAL: Ensure all text and icons are white
        const textSpan = button.querySelector('.ada-contrast-toggle-text');
        const iconSpan = button.querySelector('.ada-contrast-toggle-icon');
        if (textSpan) {
          textSpan.style.setProperty('color', '#ffffff', 'important');
        }
        if (iconSpan) {
          iconSpan.style.setProperty('color', '#ffffff', 'important');
          const svg = iconSpan.querySelector('svg');
          if (svg) {
            svg.style.setProperty('fill', '#ffffff', 'important');
            svg.style.setProperty('color', '#ffffff', 'important');
            const paths = svg.querySelectorAll('path');
            paths.forEach(path => {
              path.style.setProperty('fill', '#ffffff', 'important');
              path.style.setProperty('stroke', '#ffffff', 'important');
            });
          }
        }
      } else {
        // Inactive state: white background, black text/icons
        button.style.setProperty('background-color', '#ffffff', 'important');
        button.style.setProperty('color', '#000000', 'important');
        button.style.setProperty('border-color', '#000000', 'important');
        
        // Ensure all text and icons are black
        const textSpan = button.querySelector('.ada-contrast-toggle-text');
        const iconSpan = button.querySelector('.ada-contrast-toggle-icon');
        if (textSpan) {
          textSpan.style.setProperty('color', '#000000', 'important');
        }
        if (iconSpan) {
          iconSpan.style.setProperty('color', '#000000', 'important');
          const svg = iconSpan.querySelector('svg');
          if (svg) {
            svg.style.setProperty('fill', '#000000', 'important');
            svg.style.setProperty('color', '#000000', 'important');
            const paths = svg.querySelectorAll('path');
            paths.forEach(path => {
              path.style.setProperty('fill', '#000000', 'important');
              path.style.setProperty('stroke', '#000000', 'important');
            });
          }
        }
      }
    }
    
    // CRITICAL: Ensure button content is visible - don't hide text/icon
    // Get fresh references after footer button handling (they may have been set above)
    const textSpan = button.querySelector('.ada-contrast-toggle-text');
    const iconSpan = button.querySelector('.ada-contrast-toggle-icon');
    
    if (textSpan) {
      textSpan.style.display = 'inline-block';
      textSpan.style.visibility = 'visible';
      textSpan.style.opacity = '1';
      // Set text color based on active state with important flag
      // CRITICAL: For footer buttons, ensure white text when active (already handled above, but ensure it sticks)
      if (isActive) {
        // Active state: white text for both mobile and footer
        textSpan.style.setProperty('color', '#ffffff', 'important');
      } else {
        textSpan.style.setProperty('color', '#000000', 'important');
      }
    }
    
    if (iconSpan) {
      iconSpan.style.display = 'inline-flex';
      iconSpan.style.visibility = 'visible';
      iconSpan.style.opacity = '1';
      // Set icon color based on active state with important flag
      const svg = iconSpan.querySelector('svg');
      if (svg) {
        if (isActive) {
          // Active state: white icons for both mobile and footer
          svg.style.setProperty('fill', '#ffffff', 'important');
          svg.style.setProperty('color', '#ffffff', 'important');
          const paths = svg.querySelectorAll('path');
          paths.forEach(path => {
            path.style.setProperty('fill', '#ffffff', 'important');
            path.style.setProperty('stroke', '#ffffff', 'important');
          });
        } else {
          svg.style.setProperty('fill', '#000000', 'important');
          svg.style.setProperty('color', '#000000', 'important');
          const paths = svg.querySelectorAll('path');
          paths.forEach(path => {
            path.style.setProperty('fill', '#000000', 'important');
            path.style.setProperty('stroke', '#000000', 'important');
          });
        }
      }
    }
    
    // Update label - simplified to on/off
    let label = 'Toggle high contrast mode';
    if (mode === MODES.ON) {
      label = 'High contrast mode: ON - Click to turn off';
    } else {
      label = 'High contrast mode: OFF - Click to turn on';
    }
    button.setAttribute('aria-label', label);
    
    // Update text - keep mobile menu text as "HIGH CONTRAST" always
    // Reuse textSpan from above (line 883)
    if (textSpan) {
      const isMobileMenu = button.dataset && button.dataset.context === 'mobile-menu';
      if (isMobileMenu) {
        // Mobile menu: always use "HIGH CONTRAST"
        textSpan.textContent = 'HIGH CONTRAST';
      } else {
        // Desktop: show mode in text
        if (mode === MODES.ON) {
          textSpan.textContent = 'High Contrast (ON)';
        } else {
          textSpan.textContent = 'High Contrast';
        }
      }
    }
  }

  // Announce contrast change to screen readers
  function announceContrastChange(mode) {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.style.cssText = 'position: absolute; left: -10000px; width: 1px; height: 1px; overflow: hidden;';
    
    let message = 'High contrast mode turned off';
    if (mode === MODES.ON) {
      message = 'High contrast mode enabled';
    }
    
    announcement.textContent = message;
    document.body.appendChild(announcement);
    
    // Remove after announcement
    setTimeout(function() {
      document.body.removeChild(announcement);
    }, 1000);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initContrastControl);
  } else {
    initContrastControl();
  }

})();

